import moment from 'moment';
import { Fragment } from 'react';
import type { AlertData } from '../../types';

/**
 * Port of `timeEntryRightPanelComponents/alerts.jsx`.
 *
 * The original carried a constructor with `error`, `alerts` and `selectedAlert` state and
 * an empty `componentDidMount`; none of the three was ever read or written after
 * initialisation, so none is reproduced. The rendered DOM is unchanged.
 */

interface AlertsProps {
  alertData: AlertData | undefined;
  selectedDateYear: (date: string) => void;
}

export function Alerts({ alertData, selectedDateYear }: AlertsProps) {
  /*
   * ISO week semantics, kept exactly as written. `.isoWeekday(1)` after
   * `.startOf("isoWeek")` is redundant — startOf already lands on Monday — but it is
   * harmless and removing it would be a change for its own sake.
   */
  const getFirstDayOfWeek = (year: number, weekNumber: number): void => {
    const firstDayOfWeek = moment().year(year).isoWeek(weekNumber).startOf('isoWeek');
    const firstMonday = firstDayOfWeek.clone().isoWeekday(1);
    selectedDateYear(firstMonday.format('YYYY-MM-DD'));
  };

  return (
    <div>
      <div style={{ height: '350px', overflowY: 'auto' }}>
        {alertData?.alerts?.length ? (
          <Fragment>
            <ul className="alert-list">
              {alertData?.alerts?.map((res, index) => (
                // The original had no key here. Adding one changes no rendered attribute
                // and silences a console warning; the index matches the original ordering.
                <li
                  key={index}
                  tabIndex={0}
                  onClick={() => {
                    getFirstDayOfWeek(res.year, res.weekNo);
                  }}
                >
                  <span className="alert-point"></span>
                  {res.subject}
                </li>
              ))}
            </ul>
          </Fragment>
        ) : (
          // A <li> outside a <ul>, as in the original — App.css styles `.no-alert` on it.
          <div className="alert-list">
            <li
              className="no-alert"
              tabIndex={0}
              data-toggle="tooltip"
              data-placement="left"
              title="No data to display"
              // `alt` is not valid on an <li>; React still renders it, and it is kept so
              // the DOM matches the original exactly.
              {...{ alt: 'No data to display' }}
            >
              No data to display.
            </li>{' '}
          </div>
        )}
      </div>
    </div>
  );
}
